import { useEffect, useMemo, useState } from "react";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { endpoints } from "../lib/api";

const intervals = ["1m", "5m", "15m", "1h", "4h", "1d"];

const fmt = (n, d = 4) =>
  n === undefined || n === null
    ? "-"
    : Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

export const PriceChart = ({ symbol, onIntervalChange }) => {
  const [candles, setCandles] = useState([]);
  const [signal, setSignal] = useState(null);
  const [interval, setInterval] = useState("15m");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!symbol) return;
    setLoading(true);
    try {
      const [{ data: klines }, { data: sig }] = await Promise.all([
        endpoints.klines(symbol, interval, 150),
        endpoints.signals(symbol, interval),
      ]);
      setCandles(klines.candles);
      setSignal(sig);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = window.setInterval(load, 15000);
    return () => window.clearInterval(id);
  }, [symbol, interval]); // eslint-disable-line

  const chartData = useMemo(() => {
    if (!candles.length) return [];
    // simple EMA9/21 overlays
    const closes = candles.map((c) => c.close);
    const ema = (period) => {
      const k = 2 / (period + 1);
      const out = [];
      let prev = closes[0];
      closes.forEach((c, i) => {
        if (i === 0) out.push(c);
        else {
          const v = c * k + prev * (1 - k);
          out.push(v);
          prev = v;
        }
      });
      return out;
    };
    const ema9 = ema(9);
    const ema21 = ema(21);
    return candles.map((c, i) => ({
      time: c.time,
      timeLabel: new Date(c.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      close: c.close,
      high: c.high,
      low: c.low,
      ema9: ema9[i],
      ema21: ema21[i],
    }));
  }, [candles]);

  const priceColor =
    chartData.length > 1 && chartData.at(-1).close >= chartData[0].close
      ? "#10B981"
      : "#EF4444";

  return (
    <div className="border border-border rounded-sm bg-[#0d0d0d]">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-baseline gap-3">
          <div className="font-heading font-black text-2xl tracking-tight" data-testid="chart-symbol">
            {symbol}
          </div>
          <div className="font-mono text-lg" style={{ color: priceColor }} data-testid="chart-price">
            ${fmt(signal?.price)}
          </div>
          {signal && (
            <div
              className={`font-mono text-[10px] px-2 py-1 rounded-sm ${
                signal.decision === "BUY"
                  ? "bg-signal-buy/10 text-signal-buy border border-signal-buy/30"
                  : signal.decision === "SELL"
                  ? "bg-signal-sell/10 text-signal-sell border border-signal-sell/30"
                  : "bg-muted/20 text-muted-foreground border border-border"
              }`}
              data-testid="chart-decision"
            >
              {signal.decision} · {signal.score}
            </div>
          )}
        </div>
        <div className="flex gap-1">
          {intervals.map((iv) => (
            <button
              key={iv}
              data-testid={`interval-${iv}`}
              onClick={() => {
                setInterval(iv);
                onIntervalChange?.(iv);
              }}
              className={`font-mono text-[11px] px-2 py-1 rounded-sm border ${
                interval === iv
                  ? "border-signal-brand text-signal-brand bg-signal-brand/10"
                  : "border-border text-muted-foreground hover:text-white"
              }`}
            >
              {iv}
            </button>
          ))}
        </div>
      </div>
      <div className="h-[360px] p-2">
        {loading && !chartData.length ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Loading chart...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 40, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={priceColor} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={priceColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1a1a1a" vertical={false} />
              <XAxis
                dataKey="timeLabel"
                stroke="#555"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                minTickGap={40}
              />
              <YAxis
                domain={["dataMin", "dataMax"]}
                stroke="#555"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                orientation="right"
                width={70}
                tickFormatter={(v) => v.toFixed(v < 1 ? 6 : 2)}
              />
              <Tooltip
                contentStyle={{
                  background: "#0a0a0a",
                  border: "1px solid #222",
                  fontFamily: "JetBrains Mono",
                  fontSize: 11,
                }}
                labelStyle={{ color: "#888" }}
              />
              <Area type="monotone" dataKey="close" stroke={priceColor} strokeWidth={2} fill="url(#priceFill)" />
              <Line type="monotone" dataKey="ema9" stroke="#F59E0B" strokeWidth={1} dot={false} />
              <Line type="monotone" dataKey="ema21" stroke="#8B5CF6" strokeWidth={1} dot={false} />
              {signal?.bb_upper && (
                <ReferenceLine y={signal.bb_upper} stroke="#333" strokeDasharray="4 4" />
              )}
              {signal?.bb_lower && (
                <ReferenceLine y={signal.bb_lower} stroke="#333" strokeDasharray="4 4" />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="flex items-center gap-4 px-4 pb-3 text-[10px] font-mono text-muted-foreground">
        <div className="flex items-center gap-1">
          <span className="w-3 h-[2px] bg-[#F59E0B]" /> EMA 9
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-[2px] bg-[#8B5CF6]" /> EMA 21
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 border-t border-dashed border-muted-foreground" /> Bollinger
        </div>
      </div>
    </div>
  );
};

export default PriceChart;
